import React, { useState } from "react";
import PromptEditor from "./PromptEditor";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

const DEFAULT_PROMPT_TEMPLATE = `# {coach_name}{personality}
{meta}

# The client
{memory}{structured_memory}{base_health}{calendar}{human_coach_context}{summaries}{reflect}

{active_module_context}

# {messages}{info}

# Your task is to respond to their latest message
## You will be rewarded for the following: 
1) Your response shows a deep understanding of what has been said so far. 2) You recognize their underlying emotions and react to their cues, no matter how subtle. 3) You adjusts formality and vocabulary to align with the user's style. 4) You sometimes use micro-commitments. 5) You recognize when to slow down, change the conversation topic or modify your approach if the user seems hesitant or disengaged. 6) Your response style and length matches the user's messages (you should often have short responses). 7) You end your message after a question mark without appending anything afterwards.
## You will be penalized for the following: 
1) You ask multiple questions. 2) Your sentence structure is the same as your previous messages. 3) You overuse the user's name. 4) You overuse emojis. 5) You either append example responses or explain why you asked AFTER your question instead of ending the message on the question. 7) You speak like an AI (overly affirmative, positively reinforcing, subservient and agreeable) rather than a personal coach (understanding, assertive and contextually aware).
{paywall}

## Now, respond to the latest message with no text before or after, in plain text, never markdown, '{last_message}':
`;

// Modal for adding a new prompt blueprint
const AddTypeModal = ({ isOpen, onClose, onAdd }) => {
  const [type, setType] = useState("");

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 rounded-lg shadow-xl w-96">
        <h2 className="text-xl font-semibold mb-4">Add New Prompt Type</h2>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type
          </label>
          <input
            type="text"
            className="w-full p-2 border border-gray-300 rounded-md"
            placeholder="Enter type (e.g., voice, text)"
            value={type}
            onChange={(e) => setType(e.target.value)}
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
          >
            Cancel
          </button>
          <button
            onClick={() => {
              if (type.trim()) {
                onAdd(type.trim());
                onClose();
              }
            }}
            className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
            disabled={!type.trim()}
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
};

const PromptBlueprintEditor = ({
  value,
  onChange,
  currentProtocol,
  handleProtocolChange,
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Ensure value is an array of objects - handle backward compatibility
  const blueprints = React.useMemo(() => {
    if (!value || !Array.isArray(value) || value.length === 0) {
      // Initialize with a default text blueprint if modules are empty or invalid
      return [{ type: "text", prompt: DEFAULT_PROMPT_TEMPLATE }];
    }
    // Ensure each blueprint has a type and prompt
    return value.map((bp) => ({
      type: bp.type || "text", // Default type if missing
      prompt: bp.prompt || DEFAULT_PROMPT_TEMPLATE, // Default prompt if missing
    }));
  }, [value]);

  // This function updates the local blueprint array and calls the main onChange (handleProtocolChange('modules', ...))
  const handleBlueprintChange = (index, newPrompt) => {
    const newBlueprints = blueprints.map((bp, i) =>
      i === index ? { ...bp, prompt: newPrompt } : bp
    );
    onChange(newBlueprints); // This calls handleProtocolChange('modules', newBlueprints)
  };

  const handleAddBlueprint = (type) => {
    const newBlueprints = [
      ...blueprints,
      { type, prompt: DEFAULT_PROMPT_TEMPLATE },
    ];
    onChange(newBlueprints);
    setActiveIndex(newBlueprints.length - 1);
    setIsModalOpen(false); // Close modal explicitly
  };

  const handleRemoveBlueprint = (index) => {
    if (blueprints.length <= 1) {
      // Optionally provide feedback instead of silently failing
      console.warn("Cannot remove the last blueprint.");
      return;
    }

    // Confirmation dialog might be good here in a real app

    const newBlueprints = blueprints.filter((_, i) => i !== index);
    onChange(newBlueprints);

    // Adjust active index
    setActiveIndex((prevIndex) =>
      Math.max(0, Math.min(prevIndex, newBlueprints.length - 1))
    );
  };

  return (
    <div className="flex flex-col h-full bg-white">
      {" "}
      {/* Ensure background color */}
      {/* Tab Bar Area */}
      <div className="border-b border-gray-200 bg-slate-50 px-4 flex-shrink-0 flex items-center">
        <div className="flex overflow-x-auto py-2 gap-2 scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100">
          {" "}
          {/* Make tabs scrollable horizontally */}
          {blueprints.map((blueprint, index) => (
            <div key={index} className="relative group">
              <button
                onClick={() => setActiveIndex(index)}
                className={`px-4 py-2 rounded-lg flex items-center text-sm whitespace-nowrap transition-colors duration-150 ease-in-out ${
                  // Added text-sm and transition
                  index === activeIndex
                    ? "bg-blue-500 text-white shadow-sm" // Added shadow
                    : "bg-slate-200 text-slate-700 hover:bg-slate-300"
                }`}
                aria-current={index === activeIndex ? "page" : undefined}
              >
                {blueprint.type}
                {/* Show trash icon only if more than one blueprint exists */}
                {blueprints.length > 1 && (
                  <span
                    // Adjust margin for better alignment with button text
                    className="ml-2 -mr-1 p-0.5 rounded hover:bg-red-100 opacity-50 group-hover:opacity-100 transition-opacity"
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent setActiveIndex
                      handleRemoveBlueprint(index);
                    }}
                    aria-label={`Remove ${blueprint.type} blueprint`}
                    title={`Remove ${blueprint.type}`}
                    role="button"
                  >
                    <TrashIcon className="w-3.5 h-3.5 text-red-500" />
                  </span>
                )}
              </button>
            </div>
          ))}
          {/* Add Type Button */}
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-3 py-2 rounded-lg bg-gray-100 text-gray-600 hover:bg-gray-200 flex items-center text-sm transition-colors duration-150 ease-in-out" // Added text-sm and transition
            title="Add new blueprint type"
          >
            <PlusIcon className="w-4 h-4 mr-1" />
            Add Type
          </button>
        </div>
      </div>
      {/* Editor Area */}
      <div className="flex-1 overflow-hidden">
        {" "}
        {/* This container needs overflow-hidden */}
        {blueprints[activeIndex] && currentProtocol ? ( // Check currentProtocol exists
          <PromptEditor
            // Use key to force re-mount when index changes, ensuring state resets if needed
            key={activeIndex}
            value={blueprints[activeIndex].prompt}
            onChange={(newPrompt) =>
              handleBlueprintChange(activeIndex, newPrompt)
            }
            // Pass props down
            currentProtocol={currentProtocol}
            handleProtocolChange={handleProtocolChange}
          />
        ) : (
          <div className="p-6 text-center text-gray-500">
            Select a blueprint type or add a new one.
          </div>
        )}
      </div>
      {/* Modal for Adding Type */}
      <AddTypeModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onAdd={handleAddBlueprint}
      />
    </div>
  );
};

export default PromptBlueprintEditor;
